import React, { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import Sidebar from "./Sidebar";
import Navbar from "./Navbar";
import SocketBridge from "./SocketBridge";
import { useAuth } from "../../hooks/useAuth";
import { useSessionBootstrap } from "../../hooks/useSessionBootstrap";
import { usePinnedDocument } from "../../hooks/usePinnedDocument";
import { usePinToVisualViewport } from "../../hooks/useVisualViewport";

const AppLayout: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const { user } = useAuth();

  // Re-reads the user (role + the tenant's modules) before anything renders.
  // Without it the navigation is whatever was true at login, forever — see
  // useSessionBootstrap for why this blocks rather than correcting after paint.
  const sessionReady = useSessionBootstrap();

  // iOS leaves the document scrolled after the keyboard closes, hiding the
  // top of every page until it is reset.
  usePinnedDocument();

  // On iOS the shell is placed over whatever part of the page is actually on
  // screen, rather than at the top of the page — those differ while the
  // keyboard is up and often after it has gone. See usePinToVisualViewport.
  const [shell, setShell] = useState<HTMLDivElement | null>(null);
  usePinToVisualViewport(shell);

  // Handle responsive screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);

      // Auto-collapse sidebar on mobile
      if (mobile) {
        setIsSidebarCollapsed(true);
      }
    };

    checkScreenSize(); // Initial run
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Toggle sidebar with Ctrl/Cmd + B
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "b") {
        event.preventDefault();
        setIsSidebarCollapsed((prev) => !prev);
      }

      // Collapse sidebar on mobile with Escape
      if (event.key === "Escape" && isMobile && !isSidebarCollapsed) {
        setIsSidebarCollapsed(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isMobile, isSidebarCollapsed]);

  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => !prev);
  };

  if (!sessionReady) {
    return (
      <div className="flex h-dvh items-center justify-center bg-[#FAFAF8]">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#E5E3DD] border-t-[#C97A3C]" />
      </div>
    );
  }

  if (user?.role === "driver") {
    return (
      <div ref={setShell} className="app-shell flex bg-[#FAFAF8] overflow-hidden">
        {/* Drivers connect too — they receive work:changed on their own room. */}
        <SocketBridge />
        <div className="flex-1 flex flex-col overflow-hidden min-w-0 bg-dark-50">
          {/* `overscroll-contain` stops the scroll chaining onward when this
            reaches its end. Without it, carrying on past the bottom hands the
            gesture to the document, which rubber-bands the whole app and
            uncovers the page behind it — the blank band under a long list. */}
        <main className="flex-1 overflow-auto overscroll-contain">
            <div className="max-w-[1440px] mx-auto w-full h-full">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div ref={setShell} className="app-shell flex bg-[#FAFAF8] overflow-hidden">
      {/* Mounted ahead of <Outlet /> so the socket exists before page effects
          run — React runs sibling effects in declaration order. */}
      <SocketBridge />
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={toggleSidebar}
        isMobile={isMobile}
      />

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <Navbar
          onToggleSidebar={toggleSidebar}
          isSidebarCollapsed={isSidebarCollapsed}
          isMobile={isMobile}
        />

        {/* `overscroll-contain` stops the scroll chaining onward when this
            reaches its end. Without it, carrying on past the bottom hands the
            gesture to the document, which rubber-bands the whole app and
            uncovers the page behind it — the blank band under a long list. */}
        <main className="flex-1 overflow-auto overscroll-contain">
          <div className="max-w-[1800px] mx-auto w-full h-full">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
